import { useMode } from '@/modules/mode/composables';
import { useTargetOrgType } from '@/modules/profile/targetOrgType';
import {
  useOwnProducts,
  useOrganisationProducts,
  useSupplierAssociatedProductIDs,
  useClientAssociatedProductIDs,
} from './api';

export function useClientOrSupplierProducts(
  supplierId: MaybeRefOrGetter<string>,
  options?: {
    enabled?: () => boolean | undefined;
    type?: MaybeRefOrGetter<'client' | 'supplier'>;
  },
) {
  const type = useTargetOrgType(options?.type);
  const supplierQuery = useOrganisationProducts(supplierId, {
    enabled: toValue(type) === 'supplier' && (!options?.enabled || options.enabled() === true),
  });

  const clientQuery = useOwnProducts({
    enabled: toValue(type) === 'client' && (!options?.enabled || options.enabled() === true),
  });

  const products = computed(() =>
    toValue(type) === 'client' ? clientQuery.ownProducts.value : supplierQuery.products.value,
  );

  const productsByID = computed(() =>
    toValue(type) === 'client' ? clientQuery.productsByID.value : supplierQuery.productsByID.value,
  );

  const isLoading = computed(() =>
    toValue(type) === 'client' ? clientQuery.isLoading.value : supplierQuery.isLoading.value,
  );

  const isError = computed(() =>
    toValue(type) === 'client' ? clientQuery.isError.value : supplierQuery.isError.value,
  );

  const refetch = () => {
    if (toValue(type) === 'client') {
      return clientQuery.refetch();
    }
    return supplierQuery.refetch();
  };

  return {
    queries: [supplierQuery, clientQuery],
    products,
    productsByID,
    isLoading,
    isError,
    refetch,
  };
}

export function useSupplierOrClientAssociatedProductIDs(targetOrgId: MaybeRefOrGetter<string>) {
  const { currentMode } = useMode();

  const clientQuery = useSupplierAssociatedProductIDs(targetOrgId, {
    enabled: () => currentMode.value === 'client',
  });
  const supplierQuery = useClientAssociatedProductIDs(targetOrgId, {
    enabled: () => currentMode.value === 'supplier',
  });

  const isLoading = computed(() =>
    toValue(currentMode) === 'client' ? clientQuery.isLoading.value : supplierQuery.isLoading.value,
  );

  const isError = computed(() =>
    toValue(currentMode) === 'client' ? clientQuery.isError.value : supplierQuery.isError.value,
  );

  const associatedProductIDs = computed(() =>
    toValue(currentMode) === 'client'
      ? clientQuery.associatedProductIDs.value
      : supplierQuery.associatedProductIDs.value,
  );

  return {
    isLoading,
    isError,
    associatedProductIDs,
  };
}
